<script lang="ts">
	import { Modal, Button, Text } from '@Shelly/shelly-design-system';
	import { FLOW_TEMPLATES } from '../core/templates/flow-templates';
	import { loadReusableSubflows } from '../core/templates/reusable-subflows';

	export let showModal = false;
	export let isMobile = false;
	export let onClose: () => void;
	export let onChoose: (templateId: string) => void;
	let selectedDomain = 'All';
	$: reusableSubflows = showModal && typeof localStorage !== 'undefined' ? loadReusableSubflows() : [];
	$: domains = ['All', ...new Set(FLOW_TEMPLATES.map((template) => template.domain))];
	$: visibleTemplates =
		selectedDomain === 'All'
			? FLOW_TEMPLATES
			: FLOW_TEMPLATES.filter((template) => template.domain === selectedDomain);
</script>

<Modal
	{showModal}
	title="Start from a template"
	type={isMobile ? 'fullScreen' : 'default'}
	on:close={onClose}
>
	<svelte:fragment slot="content">
		<div class="template-content">
			<div class="intro">
				<Text variant="body" size="m"
					>Add a reusable subgraph, then configure its devices and values.</Text
				>
				<Text variant="secondary" size="sm"
					>You will choose exactly where the group is placed on the canvas.</Text
				>
			</div>
			<div class="domain-filters" aria-label="Template domains">
				{#each domains as domain}
					<button
						type="button"
						class:active={selectedDomain === domain}
						aria-pressed={selectedDomain === domain}
						on:click={() => (selectedDomain = domain)}
					>
						{domain}
					</button>
				{/each}
			</div>
			<div class="template-grid">
				{#each visibleTemplates as template}
					<button
						type="button"
						class="template-card"
						style="--template-color: {template.color}"
						on:click={() => onChoose(template.id)}
					>
						<span class="template-icon"
							><i class="fas {template.icon}" aria-hidden="true"></i></span
						>
						<span class="template-copy">
							<span class="template-domain">{template.domain}</span>
							<strong>{template.name}</strong>
							<span>{template.description}</span>
							<small>{template.outcome} · {template.nodes.length} nodes</small>
						</span>
						<i class="fas fa-chevron-right" aria-hidden="true"></i>
					</button>
				{/each}
				{#each reusableSubflows as subflow}
					<button
						type="button"
						class="template-card"
						style="--template-color: #22c55e"
						on:click={() => onChoose(`reusable:${subflow.id}`)}
					>
						<span class="template-icon"
							><i class="fas fa-puzzle-piece" aria-hidden="true"></i></span
						>
						<span class="template-copy"
							><strong>{subflow.name}</strong><span>Saved from this browser</span><small
								>{subflow.nodes.length} nodes</small
							></span
						>
						<i class="fas fa-chevron-right" aria-hidden="true"></i>
					</button>
				{/each}
			</div>
		</div>
	</svelte:fragment>
	<div slot="footer" class="modal-footer">
		<Button variant="secondary" on:click={onClose}>Cancel</Button>
	</div>
</Modal>

<style>
	.template-content {
		width: min(680px, calc(100vw - 64px));
		display: grid;
		gap: 16px;
	}
	.intro {
		display: grid;
		gap: 4px;
	}
	.domain-filters {
		display: flex;
		gap: 6px;
		overflow-x: auto;
		padding-bottom: 2px;
	}
	.domain-filters button {
		flex: 0 0 auto;
		border: 1px solid rgba(255, 255, 255, 0.12);
		border-radius: 999px;
		background: transparent;
		color: #a1a1aa;
		padding: 5px 9px;
		font-size: 11px;
		cursor: pointer;
	}
	.domain-filters button.active {
		border-color: #60a5fa;
		background: rgba(59, 130, 246, 0.14);
		color: #dbeafe;
	}
	.template-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 10px;
	}
	.template-card {
		display: grid;
		grid-template-columns: 42px minmax(0, 1fr) 14px;
		align-items: center;
		gap: 12px;
		min-height: 118px;
		padding: 13px;
		border-radius: 10px;
		border: 1px solid rgba(255, 255, 255, 0.1);
		background: rgba(255, 255, 255, 0.035);
		color: #f4f4f5;
		text-align: left;
		cursor: pointer;
	}
	.template-card:hover {
		border-color: color-mix(in srgb, var(--template-color) 65%, transparent);
		background: rgba(255, 255, 255, 0.065);
	}
	.template-icon {
		width: 42px;
		height: 42px;
		border-radius: 10px;
		display: grid;
		place-items: center;
		color: var(--template-color);
		background: color-mix(in srgb, var(--template-color) 18%, transparent);
	}
	.template-copy {
		display: grid;
		gap: 4px;
		min-width: 0;
	}
	.template-copy span {
		color: #a1a1aa;
		font-size: 12px;
		line-height: 1.35;
	}
	.template-copy .template-domain {
		width: fit-content;
		border-radius: 999px;
		background: color-mix(in srgb, var(--template-color) 16%, transparent);
		color: var(--template-color);
		padding: 2px 6px;
		font-size: 9px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}
	.template-copy small {
		color: #93c5fd;
		font-size: 10px;
	}
	.modal-footer {
		display: flex;
		justify-content: flex-end;
	}
	@media (max-width: 639px) {
		.template-content {
			width: 100%;
		}
		.template-grid {
			grid-template-columns: 1fr;
		}
	}
</style>
